import { ClipboardModule } from '@angular/cdk/clipboard';
import {
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  SecurityContext,
  signal,
  ViewChild,
} from '@angular/core';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { MarkdownPipe } from 'ngx-markdown';
import { fromEvent, Subscription, tap, throttleTime } from 'rxjs';

const COPIED_TEXT_TIMEOUT_MS = 1500;
const TOOLTIP_HIDE_FADE_MS = 200;
const SCROLL_THROTTLE_TIME_MS = 200;

@Component({
  selector: 'squadbox-message-quick-actions-copy',
  imports: [
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
    MatTooltipModule,
    ClipboardModule,
    TranslateModule,
  ],
  providers: [MarkdownPipe],
  template: `
    <button
      sdIconButton
      variant="icon"
      color="neutral"
      size="xs"
      [matTooltip]="tooltipText()"
      matTooltipPosition="above"
      (click)="onCopy()"
    >
      <svg sdUseUIBasicIcon="copy"></svg>
    </button>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class MessageQuickActionsCopyComponent implements OnInit, OnDestroy {
  private readonly markdownPipe = inject(MarkdownPipe);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly translateService = inject(TranslateService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private onScrollResetTooltipSubscription?: Subscription;
  public readonly messageContent = input.required<string>();
  public readonly tooltipText = signal(
    this.translateService.instant('CHAT.COPY'),
  );

  @ViewChild(MatTooltip) matTooltip!: MatTooltip;

  ngOnInit(): void {
    // Find the scrollable message container by traversing up the DOM
    const scrollableElement = this.findScrollableContainer();

    if (scrollableElement) {
      this.onScrollResetTooltipSubscription = fromEvent(
        scrollableElement,
        'scroll',
      )
        .pipe(
          throttleTime(SCROLL_THROTTLE_TIME_MS),
          tap(() => this.resetTooltip()),
        )
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    if (this.onScrollResetTooltipSubscription) {
      this.onScrollResetTooltipSubscription.unsubscribe();
    }
  }

  private findScrollableContainer(): Element | null {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    let element = this.elementRef.nativeElement.parentElement;

    // Traverse up the DOM tree to find the scrollable container
    while (element && element !== document.body) {
      const computedStyle = window.getComputedStyle(element as Element);
      if (
        computedStyle.overflowY === 'auto' ||
        computedStyle.overflowY === 'scroll'
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return element;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      element = element.parentElement;
    }

    return null;
  }

  public async onCopy() {
    const html = this.sanitizer.sanitize(
      SecurityContext.HTML,
      await this.markdownPipe.transform(this.messageContent()),
    );

    if (html) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([this.messageContent()], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([clipboardItem]);
    }

    this.showTooltipCopied();
  }

  private showTooltipCopied(): void {
    this.matTooltip.hideDelay = COPIED_TEXT_TIMEOUT_MS;
    this.tooltipText.set(this.translateService.instant('CHAT.COPIED'));
    this.matTooltip.show();

    setTimeout(() => {
      this.resetTooltip();
    }, COPIED_TEXT_TIMEOUT_MS);
  }

  private resetTooltip(): void {
    this.matTooltip.hideDelay = 0;
    this.matTooltip.hide();
    setTimeout(() => {
      this.tooltipText.set(this.translateService.instant('CHAT.COPY'));
    }, TOOLTIP_HIDE_FADE_MS);
  }
}
